import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Project } from '../../types/project.types';
import './ProjectModal.css';

interface ProjectModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (data: any) => Promise<void>;
  onLimitError?: () => void;
  project?: Project | null;
  title?: string;
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  show,
  onHide,
  onSave,
  onLimitError,
  project,
  title = 'Create Project'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
    setError(null);
  }, [project, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSave({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined
      });
      onHide();
    } catch (err: any) {
      // Check if this is a 403 error (project limit reached)
      const is403Error = err.response?.status === 403;

      if (is403Error && onLimitError) {
        // Close modal and show limit modal
        onHide();
        onLimitError();
      } else {
        // Show other errors inside modal
        const errorMessage = err.response?.data?.message || err.message || 'Failed to save project';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="new-project-modal">
      <Modal.Header closeButton>
        <Modal.Title>Name Project</Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Project Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter a title for your project"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              autoFocus
              maxLength={100}
            />
          </Form.Group>

        </Modal.Body>

        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={onHide} disabled={loading} className="cancel-btn">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Saving...' : project ? 'Update' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ProjectModal;